import { memo } from 'react';
import styled, { css } from 'styled-components';
import { IconComponents } from './generated/iconComponents';
import type { IIconProps } from './Icon.types';

export { IconVariants } from './iconSources';

const Icon = ({ className, variant, onClick, dataTestId, style }: IIconProps) => {
	const Svg = IconComponents[variant];

	// `variant` is typed, but ~10% of call sites compute it at runtime and can land
	// outside the union. Render nothing rather than throwing.
	if (!Svg) {
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn(`Icon: unknown variant "${variant}"`);
		}
		return null;
	}

	return (
		<Svg
			className={`${className ?? ''} ${onClick ? 'clickAnimation' : ''} svg`}
			onClick={onClick}
			style={style}
			data-testid={dataTestId}
		/>
	);
};

const Memoized = memo(Icon);
const Styled = styled(Memoized)`
	flex-shrink: 0;

	&.svg {
		box-sizing: content-box;
		--size: 0.75rem;
		width: var(--size);
		height: var(--size);
	}

	${({ size = 'md' }) => {
		switch (size) {
			case '8xl':
				return `
                    &.svg {
                        --size: 10rem;
                    }
                `;
			case '4xl':
				return `
                    &.svg {
                        --size: 6rem;
                    }
                `;
			case '3xl':
				return `
                    &.svg {
                        --size: 2.5rem;
                    }
                `;
			case 'xxl':
				return `
                    &.svg {
                        --size: 2rem;
                    }
                `;
			case 'xl':
				return `
                    &.svg {
                        --size: 1.5rem;
                    }
                `;
			case 'vlg':
				return `
                    &.svg {
                        --size: 1.25rem;
                    }
                `;
			case 'lg':
				return `
                    &.svg {
                        --size: 1rem;
                    }
                `;
			case 'md':
				return `
                    &.svg {
                        --size: 0.75rem;
                    }
                `;
			case 'sm':
				return `
                    &.svg {
                        --size: 0.5rem;
                    }
                `;
			case 'logo':
				return `
                    &.svg {
                        width: fit-content;
                        height: 1.5rem;
                    }
                `;
			default:
				return css`
					&.svg {
						--size: ${size};
					}
				`;
		}
	}}

	${({ disabled, theme }) =>
		disabled &&
		css`
			&.svg,
			&.svg path {
				color: ${theme.v2.color.neutral[500]};
				fill: ${theme.v2.color.neutral[500]};
			}
		`}

    ${({ color, fillStroke }) =>
		color &&
		css`
			&.svg,
			&.svg path {
				color: ${color};
				${fillStroke ? `stroke: ${color}` : ''};
			}
		`}

    ${({ onClick, disabled }) =>
		onClick &&
		!disabled &&
		css`
			&:hover {
				cursor: pointer;
			}
		`}
`;

export { Styled as Icon };
